// ============================================================================
// apps/web/src/lib/use-pending-absences-count.ts
// ----------------------------------------------------------------------------
// Compteur de demandes d'absence/congé « en attente » (status 'pending') —
// alimente le badge de l'entrée « Planning » (sidebar + accueil), pour que
// l'admin voie tout de suite les demandes à traiter. Réservé aux profils qui
// valident les absences (absences.manage / admin.all).
//
// Lecture directe de planning.absences (comme AbsencesPage, RLS gère l'accès).
// Rafraîchi : montage, focus/retour d'onglet, 60 s, et notifyAbsencesChanged().
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth-context';
import { Capability } from '@magic/shared';

const EVENT = 'magic:absences-changed';

export function notifyAbsencesChanged(): void {
  window.dispatchEvent(new Event(EVENT));
}

export function usePendingAbsencesCount(): number {
  const { hasCapability } = useAuth();
  const canSee = hasCapability(Capability.ABSENCES_MANAGE) || hasCapability(Capability.ADMIN_ALL);
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!canSee) { setCount(0); return; }
    try {
      const { count: c } = await supabase
        .schema('planning')
        .from('absences')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      setCount(c ?? 0);
    } catch { /* silencieux : le badge n'est pas critique */ }
  }, [canSee]);

  useEffect(() => {
    if (!canSee) { setCount(0); return; }
    void refresh();
    const onFocus = () => { void refresh(); };
    const onVis = () => { if (document.visibilityState === 'visible') void refresh(); };
    const onEvt = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(EVENT, onEvt);
    const id = window.setInterval(() => { void refresh(); }, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(EVENT, onEvt);
      window.clearInterval(id);
    };
  }, [canSee, refresh]);

  return count;
}
